import React, { useMemo, useState, useContext } from 'react'
import { omitBy, isUndefined } from '@golden/utils'
import { PiggyWechat, DepositMethodType, PaginationRes } from '@golden/gdk-admin'
import LoadingAndErrorFrame from '../../default/frames/LoadingAndErrorFrame'
import CoreTable from '../../default/present/CoreTable'
import { usePageFlow } from '../../../utils/default/PageFlowHook'
import { useCommonStyles } from '../../../utils/admin/StyleHook'
import useT from '../../../i18ns/admin/useT'
import { useGetDataByPayload, useReload } from '../../../utils/default/ComplexFlowHook'
import useGDK from '../../../providers/admin/gdk/useGDK'
import { createTableData, formatMoney, createDefaultPaginationData } from '../../../utils/default/TableHelper'
import depositTypeName from '../../../constants/default/depositTypeName'
import { useChecker } from '../../../utils/admin/AdminRouteHook'
import DateTime from '../../default/present/DateTime'
import TableStatus from '../../default/present/TableStatus'
import { FormContext } from '../../../views/admin/finance/FinancePiggyWechatPage'
import { convertEmptyToUndefined } from '../../../utils/default/FormHelper'
import FinancePiggyWechatButtons from './FinancePiggyWechatButtons'
import clientTypeName from '../../../constants/admin/clientTypeName'

interface RowType {
  id: number
  layer: React.ReactElement
  name: string
  account: string
  group: string
  way: string
  min: string
  max: string
  receiver: string
  memo: string
  client: React.ReactElement
  status: React.ReactElement
  updatedAt: React.ReactElement
  updatedBy: string
  functions: React.ReactElement
}

const FinancePiggyWechatTable: React.FC = () => {
  const [form, setForm] = useContext(FormContext)
  const classes = useCommonStyles()
  const [list, setList] = useState<PaginationRes<PiggyWechat[]>>(createDefaultPaginationData([]))
  const pageFlow = usePageFlow()
  const gdk = useGDK()
  const { t } = useT()
  const writable = useChecker()
  const { reload, reloadFlag } = useReload()
  const payload = useMemo(() => {
    const payload = omitBy({ account: convertEmptyToUndefined(form.account) }, isUndefined)
    if (form.status === '--') return { ...payload, page: form.page }
    return { ...payload, is_active: Number(form.status) as 0 | 1, page: form.page }
  }, [form])
  useGetDataByPayload({
    payload,
    gdkFunc: (payload) => gdk.finance.getPiggyWechatList(payload),
    gdkFuncDependencies: [gdk, reloadFlag],
    onBeforeFetch: pageFlow.setLoadingStart,
    onSuccess: (res: PaginationRes<PiggyWechat[]>) => {
      setList(res)
      pageFlow.setContentShow()
    },
    onError: pageFlow.setGDKError
  })

  const tableClasses = useMemo(() => {
    return {
      head: classes.pinkTableHead,
      row: classes.tableRow,
      cellBody: classes.nowrap,
      cellHead: classes.tableCellHead
    }
  }, [classes])
  const rows = useMemo(() => {
    return list.data.map((item) => {
      return {
        id: item.id,
        layer: (
          <React.Fragment>
            {item.layers.map((layer) => (<p key={`layer-${layer.id}`}>{layer.name}</p>))}
          </React.Fragment>
        ),
        name: item.name,
        account: item.account,
        group: item.group.name ?? '-',
        way: t(depositTypeName[DepositMethodType.SCAN_WECHAT]),
        min: formatMoney(item.cash_min),
        max: formatMoney(item.cash_max),
        receiver: item.real_name ?? '',
        memo: item.memo,
        client: (
          <React.Fragment>
            {item.clients.map((client) => (
              <p>{t(clientTypeName[client])}</p>
            ))}
          </React.Fragment>
        ),
        status: (<TableStatus status={item.is_active} />),
        updatedAt: (<DateTime time={item.updated_at} />),
        updatedBy: item.updated_by,
        functions: (
          <FinancePiggyWechatButtons
            item={item}
            reload={reload}
          />
        )
      } as RowType
    })
  }, [t, list, reload])

  const data = useMemo(() => {
    return createTableData<RowType>(
      {
        id: {
          label: '',
          value: 'id'
        },
        layer: {
          label: t('common.openLayer'),
          value: 'layer',
          align: 'center'
        },
        name: {
          label: t('common.wechatName'),
          value: 'name',
          align: 'center'
        },
        account: {
          label: t('common.wechatAccount'),
          value: 'account',
          align: 'center'
        },
        way: {
          label: t('common.financeDepositWay'),
          value: 'way',
          align: 'center'
        },
        min: {
          label: t('common.minDepositMoney'),
          value: 'min',
          align: 'right'
        },
        max: {
          label: t('common.maxDepositMoney'),
          value: 'max',
          align: 'right'
        },
        receiver: {
          label: t('common.playerSideReceiver'),
          value: 'receiver',
          align: 'center'
        },
        memo: {
          label: t('common.memoForPlayer'),
          value: 'memo',
          align: 'center'
        },
        client: {
          label: t('common.platformVersion'),
          value: 'client',
          align: 'center'
        },
        group: {
          label: t('common.depositWayGroupShort'),
          value: 'group',
          align: 'center'
        },
        status: {
          label: t('common.status'),
          value: 'status',
          align: 'center'
        },
        updatedAt: {
          label: t('common.updateAt'),
          value: 'updatedAt',
          align: 'center'
        },
        updatedBy: {
          label: t('common.updateBy'),
          value: 'updatedBy',
          align: 'center'
        },
        functions: {
          label: t('common.function'),
          value: 'functions',
          align: 'center'
        }
      },
      writable
        ? [
            'layer',
            'name',
            'account',
            'way',
            'min',
            'max',
            'receiver',
            'memo',
            'client',
            'group',
            'status',
            'updatedAt',
            'updatedBy',
            'functions'
          ]
        : [
            'layer',
            'name',
            'account',
            'way',
            'min',
            'max',
            'receiver',
            'memo',
            'client',
            'group',
            'status',
            'updatedAt',
            'updatedBy'
          ],
      rows,
      'id'
    )
  }, [t, rows, writable])

  return (
    <LoadingAndErrorFrame { ...pageFlow.status }>
      <CoreTable
        classes={tableClasses}
        data={data}
        showPagination
        total={list.total}
        page={form.page}
        onChangePage={(_, page) => { setForm((form) => ({ ...form, page })) }}
      />
    </LoadingAndErrorFrame>
  )
}

export default React.memo(FinancePiggyWechatTable)
